import { useState } from "react";
import { useGetList } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  FileText, Plus, Cloud, Users, Clock, AlertTriangle,
} from "lucide-react";
import { DailyReportForm } from "./DailyReportForm";

interface DailyReport {
  id: string;
  report_date: string;
  summary: string;
  work_completed: string;
  issues: string;
  weather: string;
  crew_count: number;
  hours_worked: number;
  delay_flag: boolean;
  delay_reason: string;
  delay_hours: number;
  created_at: string;
}

interface DailyReportListProps {
  jobId: string;
  contactId?: string | number;
  readOnly?: boolean;
}

function formatDate(iso: string): string {
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
}

export function DailyReportList({ jobId, contactId, readOnly = false }: DailyReportListProps) {
  const [formOpen, setFormOpen] = useState(false);

  const { data, isPending } = useGetList<DailyReport>("daily_reports", {
    pagination: { page: 1, perPage: 60 },
    sort: { field: "report_date", order: "DESC" },
    filter: { job_id: jobId },
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <FileText className="w-4 h-4" />
          Daily Reports
          {data && data.length > 0 && (
            <span className="text-xs text-muted-foreground font-normal">({data.length})</span>
          )}
        </h4>
        {!readOnly && (
          <button onClick={() => setFormOpen(true)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-blue-600 hover:bg-blue-50">
            <Plus className="w-3 h-3" /> New Report
          </button>
        )}
      </div>

      {isPending && <p className="text-xs text-muted-foreground">Loading…</p>}

      {!isPending && (!data || data.length === 0) && (
        <p className="text-xs text-muted-foreground">No daily reports yet.</p>
      )}

      {data && data.length > 0 && (
        <div className="space-y-1.5">
          {data.map((r) => (
            <Card key={r.id} className="shadow-none">
              <CardContent className="p-3">
                <div className="flex items-start justify-between mb-1">
                  <p className="text-xs font-semibold">{formatDate(r.report_date)}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {r.weather && (
                      <span className="flex items-center gap-0.5">
                        <Cloud className="w-3 h-3" /> {r.weather}
                      </span>
                    )}
                    <span className="flex items-center gap-0.5">
                      <Users className="w-3 h-3" /> {r.crew_count}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <Clock className="w-3 h-3" /> {r.hours_worked}h
                    </span>
                  </div>
                </div>
                <p className="text-sm">{r.summary}</p>
                {r.work_completed && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="font-medium">Done:</span> {r.work_completed}
                  </p>
                )}
                {r.issues && (
                  <p className="text-xs text-red-600 mt-1">
                    <span className="font-medium">Issues:</span> {r.issues}
                  </p>
                )}
                {r.delay_flag && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    <AlertTriangle className="w-3 h-3" />
                    Delay: {r.delay_reason || "Unspecified"} ({r.delay_hours}h)
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DailyReportForm open={formOpen} onClose={() => setFormOpen(false)}
        jobId={jobId} contactId={contactId} />
    </div>
  );
}
